/**
 * Map Operations - Funktioner för att hantera map operations
 * 
 * Använder map-state.js för state och label-operations.js för operations
 */

import * as mapState from './map-state';
import * as labelOps from './label-operations';
import * as districtCache from '../districts/district-cache';
import { booleanPointInPolygon, point as turfPoint } from '../utils/turf';

// Externa handlers (kan sättas från React-hookar för att synca state m.m.)
let externalHandlers = {};

export function setExternalHandlers(handlers) {
    externalHandlers = handlers || {};
}

export function getExternalHandlers() {
    return externalHandlers;
}

// Closure för att hålla click area listener
let clickAreaListener = null;

/** Senaste bounds som urvalslager-/SD-labels byggdes för (overlay skapar bara DOM inom bounds vid onAdd). */
let lastBoundsForLabels = null;

/**
 * Kolla om det finns synliga sales districts i cachen
 * @returns {boolean} True om det finns minst ett synligt sales district i cachen
 */
function hasVisibleSalesDistricts() {
    const allDistricts = districtCache.getAllDistrictsForScenario();
    if (!allDistricts || allDistricts.length === 0) {
        return false;
    }
    // Kolla om det finns minst ett distrikt med visible: true
    return allDistricts.some(district => district.visible === true);
}


/**
 * Remove area click event listener
 * @param {Object} listener - Listener instance
 */
function removeAreaClickEvent(listener) {
    const maps = mapState.getMaps();
    if (listener) {
        maps.event.removeListener(listener);
    }
}

/**
 * Find the turfEntry whose polygon contains the given point.
 * Uses cached bounding boxes for fast O(n) pre-filtering before the
 * expensive booleanPointInPolygon test.
 */
function findTurfEntryAtPoint(lng, lat, selectionType) {
    const entries = mapState.getTurfPolygons(selectionType);
    if (!entries) return null;

    const pt = turfPoint([lng, lat]);

    for (const entry of entries) {
        if (!entry._bbox) {
            const nums = entry.polygon.geometry.coordinates.flat(Infinity);
            let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
            for (let i = 0; i < nums.length; i += 2) {
                const x = nums[i], y = nums[i + 1];
                if (x < minLng) minLng = x;
                if (x > maxLng) maxLng = x;
                if (y < minLat) minLat = y;
                if (y > maxLat) maxLat = y;
            }
            entry._bbox = [minLng, minLat, maxLng, maxLat];
        }

        const [bMinLng, bMinLat, bMaxLng, bMaxLat] = entry._bbox;
        if (lng < bMinLng || lng > bMaxLng || lat < bMinLat || lat > bMaxLat) continue;

        if (booleanPointInPolygon(pt, entry.polygon)) {
            return entry;
        }
    }
    return null;
}

/**
 * Attach area click event listener.
 * Uses map-level click (not map.data click) to avoid Google Maps'
 * expensive O(n) hit detection on all data-layer features.
 */
function attachAreaClickEvent(map, onAreaClick, getSelectionType) {
    const listener = map.addListener('click', (event) => {
        if (!mapState.isAreaClickEnabled()) return;
        const selectionType = getSelectionType();
        if (selectionType === 'clear') return;

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        const entry = findTurfEntryAtPoint(lng, lat, selectionType);
        if (entry) {
            onAreaClick(entry.feature);
        }
    });

    return listener;
}

/**
 * Initialize map när Google Maps API är laddad
 * @param {Object} params - { map, maps, onAreaClick }
 * @param {Object} params.map - Google Maps map instance
 * @param {Object} params.maps - Google Maps API instance
 * @param {Function} params.onAreaClick - Callback för när area klickas
 */
export async function initializeMap({ map, maps, onAreaClick }) {
    if (clickAreaListener) {
        removeAreaClickEvent(clickAreaListener);
        clickAreaListener = null;
    }
    
    // Initiera map state
    mapState.initMapState({ map, maps });
    lastBoundsForLabels = null;

    map.addListener('zoom_changed', () => {
        labelOps.clearOverlayDom();
    });

    map.addListener('idle', () => {
        const bounds = map.getBounds();
        // Urvalslager-labels filtreras i overlay.onAdd till det som låg inom bounds då.
        // Vid pan ändras inte zoom → ingen clear från zoom_changed → cachen i handleLabels
        // hoppar över rebuild. Töm därför DOM när viewport (bounds) ändrats så labels byggs om.
        if (bounds && (!lastBoundsForLabels || !lastBoundsForLabels.equals(bounds))) {
            lastBoundsForLabels = bounds;
            labelOps.clearOverlayDom();
        }
        const selectionType = districtCache.getCurrentDistrictSelectionType();
        labelOps.handleLabels({
            map,
            maps,
            turfPolygons: mapState.getAllTurfPolygons(),
            selectionType,
            isSalesDistricts: hasVisibleSalesDistricts(),
            salesDistricts: mapState.getSalesDistricts()
        });
    });

    // Undvik map.data.setStyle({ ... }) här: ett globalt style-objekt gör att
    // Terra Draw-adapterns style-funktion aldrig registreras (getStyle() är satt),
    // vilket ger standard Google-markörer vid polygonhörn och fel linjefärg.
    // clickable: false sätts via overrideStyle på urvalslager (selection-type-operations m.m.).

    clickAreaListener = attachAreaClickEvent(
        map,
        onAreaClick,
        () => districtCache.getCurrentDistrictSelectionType()
    );

    // Force initial style update
    applyStyles();
}


function applyActiveDistrictStyles(featureIdsToStyle, selectedAreaIds, selectionType, currentDistrict) {
    const map = mapState.getMap();

    for (const id of featureIdsToStyle) {
        const feature = map.data.getFeatureById(id);
        if (!feature) continue;

        const featureType = feature.getProperty('type');
        const isSelected = selectedAreaIds.has(id);

        if (isSelected) {
            const isActiveLayer = featureType === selectionType;
            map.data.overrideStyle(feature, {
                strokeWeight: 1.5,
                fillOpacity: 0.4,
                strokeColor: isActiveLayer ? '#cc0000' : 'transparent',
                visible: true,
                zIndex: isActiveLayer ? 4 : 1,
                fillColor: currentDistrict.color,
                clickable: false,
            });
        } else {
            map.data.overrideStyle(feature, {
                strokeWeight: 1.5,
                fillColor: '#ff0000',
                fillOpacity: 0,
                strokeColor: '#cc0000',
                visible: featureType === selectionType,
                clickable: false,
            });
        }
    }
}

/**
 * Apply styles to map features based on selected areas
 * Safe to call even if map is not initialized (will silently return)
 */
export function applyStyles() {
    if (!mapState.isInitialized()) {
        return;
    }

    const map = mapState.getMap();
    if (!map || !map.data) {
        return;
    }
    
    // Använd requestAnimationFrame för att batcha style-uppdateringar
    const currentFrame = mapState.getStyleUpdateFrame();
    if (currentFrame) {
        cancelAnimationFrame(currentFrame);
    }
    
    const frameId = requestAnimationFrame(() => {
        const selectedAreas = districtCache.getCurrentDistrictSelectedAreas();
        const selectionType = districtCache.getCurrentDistrictSelectionType();
        const currentDistrict = districtCache.getCurrent();
        const previousSelectedAreaIds = mapState.getPreviousSelectedAreaIds();
        
        const currentSelectedAreaIds = new Set();
        if (selectedAreas && selectedAreas.length > 0) {
            for (const area of selectedAreas) {
                if (area?.properties?.id) {
                    currentSelectedAreaIds.add(area.properties.id);
                }
            }
        }

        const previousSelectionType = mapState.getPreviousSelectionType();
        const selectionTypeChanged = selectionType !== previousSelectionType;

        // Skip work if both selected IDs and selection type are identical to previous frame
        if (!selectionTypeChanged && currentSelectedAreaIds.size === previousSelectedAreaIds.size) {
            let identical = true;
            for (const id of currentSelectedAreaIds) {
                if (!previousSelectedAreaIds.has(id)) { identical = false; break; }
            }
            if (identical) {
                mapState.setStyleUpdateFrame(null);
                return;
            }
        }

        let featureIdsToStyle;

        if (selectionTypeChanged || previousSelectedAreaIds.size === 0) {
            // Full restyle: selection type changed or force-apply (previousIds was cleared)
            featureIdsToStyle = currentSelectedAreaIds;
        } else {
            // Incremental: only style the symmetric difference (added + removed)
            featureIdsToStyle = new Set();
            for (const id of currentSelectedAreaIds) {
                if (!previousSelectedAreaIds.has(id)) featureIdsToStyle.add(id);
            }
            for (const id of previousSelectedAreaIds) {
                if (!currentSelectedAreaIds.has(id)) featureIdsToStyle.add(id);
            }
        }

        applyActiveDistrictStyles(featureIdsToStyle, currentSelectedAreaIds, selectionType, currentDistrict);

        mapState.setPreviousSelectedAreaIds(new Set(currentSelectedAreaIds));
        mapState.setPreviousSelectionType(selectionType);
        
        mapState.setStyleUpdateFrame(null);
    });
    
    mapState.setStyleUpdateFrame(frameId);
}

/**
 * Force re-apply styles regardless of skip optimization.
 * Use when visual properties (e.g. district color) change without affecting area IDs or selection type.
 */
export function forceApplyStyles() {
    mapState.setPreviousSelectedAreaIds(new Set());
    mapState.setPreviousSelectionType(null);
    applyStyles();
}

/**
 * Zoom map to fit selected areas or specific district IDs
 * @param {Array|null} districtIds - Optional array of district IDs to zoom to. If null, zooms to all selected areas
 * @throws {Error} If map is not initialized
 */
export function zoomToDistrict(districtIds = null) {
    if (!mapState.isInitialized()) {
        const error = new Error('map-operations: Maps not initialized. Call initializeMap first.');
        console.warn(error.message);
        throw error;
    }

    const map = mapState.getMap();
    const maps = mapState.getMaps();
    const selectionType = districtCache.getCurrentDistrictSelectionType();
    const districtTypes = districtCache.getCurrentDistrictTypes();

    const bounds = new maps.LatLngBounds();
    let totalShapes = 0;
    let matchingShapes = 0;
    let shapesWithGeometry = 0;

    map.data.forEach((shape) => {
        totalShapes++;
        const shapeId = shape.getProperty('id');
        const shapeType = shape.getProperty('type');
        const shouldInclude = districtIds === null || (districtIds && districtIds.indexOf(shapeId) > -1);
        
        if (shouldInclude) {
            matchingShapes++;
            const geometry = shape.getGeometry();
            if (geometry) {
                shapesWithGeometry++;
                if (geometry.getType() === 'GeometryCollection') {
                    geometry.getArray().forEach(inner => {
                        inner.forEachLatLng(latlng => {
                            bounds.extend(latlng);
                        });                    
                    });
                } else {
                    geometry.forEachLatLng((latlng) => {
                        bounds.extend(latlng);
                    });
                }
            } else {
                console.warn('[zoomToDistrict] Shape has no geometry:', { shapeId, shapeType });
            }
        }
    });


    if (bounds.getNorthEast() && bounds.getSouthWest()) {
        console.log('[zoomToDistrict] Calling fitBounds');
        map.fitBounds(bounds);
    } else {
        console.error('[zoomToDistrict] Bounds are empty, cannot zoom!', {
            districtIds,
            totalShapes,
            matchingShapes,
            shapesWithGeometry,
            selectionType,
            districtTypes
        });
    }
}

/**
 * Update labels när sales districts ändras
 */
export function updateLabels() {
    const map = mapState.getMap();
    const maps = mapState.getMaps();
    const selectionType = districtCache.getCurrentDistrictSelectionType();
    
    labelOps.handleLabels({
        map,
        maps,
        turfPolygons: mapState.getAllTurfPolygons(),
        selectionType,
        isSalesDistricts: hasVisibleSalesDistricts(),
        salesDistricts: mapState.getSalesDistricts()
    });
}

/**
 * Kör callback efter nästa map idle (tiles + projektion stabila).
 * Anropar handleLabels innan callback så label-DOM får korrekta pixelkoordinater.
 *
 * Om kartan redan är idle när vi lyssnar triggas `idle` ofta inte igen — då skulle laddningen aldrig avslutas.
 * Därför: `map.resize()` (omritar utan att flytta centrum) så en ny idle-cykel brukar komma,
 * plus en engångs requestAnimationFrame som sista säkerhetsnät (runOnce förhindrar dubbel körning).
 */
export function runAfterMapIdle(callback) {
    const map = mapState.getMap();
    const maps = mapState.getMaps();
    if (!map || !maps) {
        if (callback) callback();
        return;
    }
    const run = () => {
        labelOps.handleLabels({
            map,
            maps,
            turfPolygons: mapState.getAllTurfPolygons(),
            selectionType: districtCache.getCurrentDistrictSelectionType(),
            isSalesDistricts: hasVisibleSalesDistricts(),
            salesDistricts: mapState.getSalesDistricts()
        });
        if (callback) callback();
    };
    let finished = false;
    const runOnce = () => {
        if (finished) return;
        finished = true;
        run();
    };
    maps.event.addListenerOnce(map, 'idle', runOnce);
    if (typeof map.resize === 'function') {
        map.resize();
    }
    requestAnimationFrame(() => {
        runOnce();
    });
}


// Exportera mapState funktioner för enkel access
export {
    getMap,
    getMaps,
    isInitialized,
    getTurfPolygons,
    getAllTurfPolygons,
    getTurfEntryById,
    getLayerFeatureIds,
    hasLayerType,
    updateTurfPolygons,
    setTools,
    getMapState,
    setSalesDistricts,
    getSalesDistricts,
    getHistory,
    undo,
    setTypeAndScenario
} from './map-state';
